/**
 * Feb 2026 (S-2 401 regression fix) — Shared blob-download opener
 * for authenticated static-asset endpoints.
 *
 * Background: after S-2 narrowed the access-token cookie to `/api/auth`,
 * plain `<a href="${backend}/api/docs/...">` links no longer authenticate
 * because the cookie isn't sent and an anchor can't carry a Bearer
 * header. This helper:
 *
 *   1. Opens a blank tab SYNCHRONOUSLY on the click event (bypasses
 *      popup blockers, which require a user-gesture stack frame).
 *   2. Fetches the resource via the authenticated `api` client with
 *      `responseType: "blob"` (Bearer token attached automatically).
 *   3. Wraps the blob in `URL.createObjectURL(...)` and points the
 *      already-open tab at it.
 *   4. Revokes the object URL after 60s (browsers hold the blob open
 *      via the tab's document, so revocation doesn't kill the PDF; it
 *      just releases the JS-side handle so the GC can free the buffer
 *      once the user closes the tab).
 *
 * On error the fallback tab is closed and a caller-supplied `onError`
 * is invoked (typically a `toast.error(...)`).
 *
 * Usage:
 *   <button onClick={() => openAuthenticatedInNewTab(
 *     "/docs/ops-manager-manual",
 *     { onError: (e) => toast.error(...), }
 *   )}>Open manual</button>
 */
import api from "@/lib/api";

const _REVOKE_DELAY_MS = 60_000;

export async function openAuthenticatedInNewTab(path, { onError, onStart, onDone } = {}) {
  // Step 1 — open blank tab synchronously to keep the user-gesture
  // stack frame; popup blockers reject `window.open` when it's
  // called from an async continuation.
  const tab = typeof window !== "undefined" ? window.open("", "_blank", "noopener,noreferrer") : null;
  if (tab) {
    // Minimal loading UX inside the fallback tab. Prevents a jarring
    // "about:blank" flash while the fetch runs.
    try {
      tab.document.write(
        '<!doctype html><meta name="referrer" content="no-referrer">' +
        '<title>Loading…</title>' +
        '<body style="background:#0c0c0c;color:#a1a1aa;font-family:system-ui;padding:2rem">' +
        '<div>Loading…</div></body>',
      );
      tab.document.close();
    } catch {
      /* cross-origin write failure is silent — the tab was opened
         same-origin so this normally succeeds */
    }
  }

  onStart?.();
  try {
    const r = await api.get(path, { responseType: "blob" });
    const blob = r.data;
    const objectUrl = URL.createObjectURL(blob);
    if (tab && !tab.closed) {
      tab.location.href = objectUrl;
    } else {
      // Popup blocked or user closed the placeholder — fall back to
      // same-tab navigation. Rare but the object URL is still valid.
      if (typeof window !== "undefined") window.location.href = objectUrl;
    }
    // Delay revocation so the tab has time to load the blob. The blob
    // remains readable via the tab's own document reference even after
    // revocation of the JS-side URL handle.
    setTimeout(() => URL.revokeObjectURL(objectUrl), _REVOKE_DELAY_MS);
    onDone?.();
    return true;
  } catch (err) {
    if (tab && !tab.closed) {
      try { tab.close(); } catch { /* noop */ }
    }
    onError?.(err);
    return false;
  }
}

export default openAuthenticatedInNewTab;
